import DOMPurify from "dompurify";

// NOTE: We avoid letting the URL regex consume HTML tags (e.g. <br />) that
// may have been injected before linkifying. The previous pattern used [^\s]+,
// which allowed '<' characters and caused a pasted URL followed immediately by
// a newline (later transformed into <br />) to swallow the <br /> and the text
// that followed, turning the whole message into one long link.
// New rule: stop a URL at the first whitespace OR a '<'.

// More comprehensive URL regex that includes common URL patterns
const COMPREHENSIVE_URL_REGEX = /(https?:\/\/(?:[-\w.])+(?::[0-9]+)?(?:\/(?:[\w/_.%-])*)?(?:\?(?:[\w&=%.+-])*)?(?:#(?:[\w.%-])*)?)/g;

const createAnchor = (url) => {
  // Clean the URL to remove trailing punctuation that shouldn't be part of the link
  const cleanUrl = url.replace(/[.,;:!?)]$/, '');
  return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" class="text-blue-800 underline">${cleanUrl}</a>`;
};

const processReferenceWithUrls = (referenceText) => {
  if (!referenceText || typeof referenceText !== 'string') return referenceText;

  // Avoid re-linking already linked content
  if (referenceText.includes('<a ') && referenceText.includes('</a>')) {
    return DOMPurify.sanitize(referenceText, { ADD_ATTR: ['target'] });
  }

  const linked = referenceText.replace(COMPREHENSIVE_URL_REGEX, (url) => createAnchor(url));
  return DOMPurify.sanitize(linked, { ADD_ATTR: ['target'] });
};

const linkify = (text) => {
  if (!text || typeof text !== 'string') return text;

  // Avoid re-linking already linked content
  if (text.includes('<a ') && text.includes('</a>')) {
    return DOMPurify.sanitize(text, { ADD_ATTR: ['target'] });
  }

  // We assume newlines have possibly already been converted to <br /> outside.
  // Only replace raw URLs; do NOT re-link inside existing anchors.
  const linked = text.replace(COMPREHENSIVE_URL_REGEX, (url) => createAnchor(url));
  return DOMPurify.sanitize(linked, { ADD_ATTR: ['target'] });
};

// Function to ensure URLs starting with www. get proper protocol (only when not already present)
const ensureUrlsLinked = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  // Only process www. URLs that don't already have a protocol
  // This is mainly for cases where someone writes "www.example.com" without https://
  const wwwWithoutProtocolRegex = /(^|[^\w:/.])(www\.[-\w.]+(?:[/[\w/_.]*)?)(?=\s|$|[<>"])/g;
  
  return text.replace(wwwWithoutProtocolRegex, (match, prefix, url) => {
    return `${prefix}https://${url}`;
  });
};

export default linkify;
export { processReferenceWithUrls, ensureUrlsLinked };